import { action, makeObservable, observable } from 'mobx';
import { Country } from 'react-phone-number-input';

import { parsedUrlSearchArrayValuesToSingleValues, parseUrlSearch } from 'helpers/utils';
import { BackendPublicEnums } from 'public-api/codegen';
import { ApiClient as PublicApiClient } from 'public-api/codegen/api';
import { signupLanguage } from 'ui-brand/languages';

import getFormErrors from './formErrors';

type Step = 'EMAIL' | 'DETAILS' | 'PRODUCTS';

type Errors = Partial<Record<keyof BackendPublic.AccountRegisterRequest, string>>;

const STEP_FIELDS: Record<Step, (keyof BackendPublic.AccountRegisterRequest)[]> = {
  EMAIL: ['email'],
  DETAILS: ['shop_url', 'account_name', 'full_name', 'password', 'phone_number'],
  PRODUCTS: ['selected_products'],
};

class RegistrationStore {
  @observable data: BackendPublic.AccountRegisterRequest = {
    email: '',
    password: '',
    full_name: '',
    account_name: '',
    shop_url: '',
    phone_number: '',
    selected_products: [],
    language: signupLanguage().code as BackendPublicEnums.AccountRegisterLanguageEnum,
    portal: false,
  };
  @observable phoneCountry: Country | undefined = signupLanguage().country;
  @observable errors: Errors = {};
  @observable step: Step = 'EMAIL';
  @observable isEmailTaken = false;

  private api: PublicApiClient;

  constructor(api: PublicApiClient) {
    makeObservable(this);
    this.api = api;
  }

  @action.bound loadFromSearch(search: string) {
    const searchParams = parsedUrlSearchArrayValuesToSingleValues(parseUrlSearch(search)) as Record<string, string>;
    this.data.email = searchParams.email || '';
    this.data.portal = Boolean(searchParams.portal);
    this.data.utm_tags = {
      utm_source: searchParams.utm_source,
      utm_medium: searchParams.utm_medium,
      utm_content: searchParams.utm_content,
      utm_campaign: searchParams.utm_campaign,
    };
  }

  @action.bound updateData(data: Partial<BackendPublic.AccountRegisterRequest>) {
    this.data = { ...this.data, ...data };
    (Object.keys(data) as (keyof Errors)[]).forEach((field) => {
      delete this.errors[field];
    });
  }

  @action.bound setEmail(email: string) {
    this.updateData({ email });
    this.isEmailTaken = false;
  }

  @action.bound setPhoneCountry(country?: Country) {
    this.phoneCountry = country;
  }

  @action.bound async submitEmail() {
    const email = this.data.email;
    try {
      const isAvailable = Boolean(await this.api.AuthEmailAvailableApi.retrieve({ email }));
      if (email !== this.data.email) return;
      this.isEmailTaken = !isAvailable;
      if (isAvailable) this.step = 'DETAILS';
    } catch (error) {
      if (email !== this.data.email) return;
      const errors = await getFormErrors(error);
      if (email === this.data.email) this.errors = errors;
    }
  }

  @action.bound async submitDetails() {
    try {
      await this.api.AuthRegisterValidateDetailsApi.create({ request: this.data });
      this.step = 'PRODUCTS';
    } catch (error) {
      this.errors = await getFormErrors(error);
    }
  }

  @action.bound goBack() {
    this.step = this.step === 'PRODUCTS' ? 'DETAILS' : 'EMAIL';
  }

  @action.bound async register() {
    try {
      await this.api.AuthRegisterApi.create({ request: this.data });
      return true;
    } catch (error) {
      this.errors = await getFormErrors(error);
      const invalidStep = (Object.keys(STEP_FIELDS) as Step[]).find((step) =>
        STEP_FIELDS[step].some((field) => field in this.errors),
      );
      if (invalidStep) this.step = invalidStep;
      return false;
    }
  }
}

export default RegistrationStore;
